import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import { tableSchema, tableSchemaGroups } from '../Table';
import PricesTable from './PricesTable';

const schema: JSONSchema7 = {
    title: 'Prices Table',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        ...tableSchema.properties,
        dateDatakey: {
            type: 'string',
            description: 'Data key for date value',
            default: 'effectiveDate',
        },
        filterButtonName: {
            type: 'string',
            description: 'Filter button text',
            default: 'Filter',
        },
        startDateLabel: {
            type: 'string',
            description: 'Start Date label',
            default: 'Start Date',
        },
        endDateLabel: {
            type: 'string',
            description: 'End Date label',
            default: 'End Date',
        },
        downloadButtonName: {
            type: 'string',
            description: 'Download button text',
            default: 'Download',
        },
        transformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        displayDownloadButtonTop: {
            type: 'boolean',
            title: 'Display Download Button Top',
            default: false,
        },
        downloadFileName: {
            type: 'string',
            title: 'Download File Name',
            default: '',
        },
        hideColumnsInCsv: {
            type: 'string',
            title: 'Hide Columns in Csv',
            description: 'Enter comma seperated column numbers',
            default: '',
        },
        fileType: {
            type: 'string',
            enum: ['.xlsx', '.csv'],
            title: 'Download File Type',
        },
        isUseIntegratedDatePicker: {
            type: 'boolean',
            title: 'Is Use Integrated Date Picker',
            default: false,
        },
    },
    dependencies: {
        fileType: {
            oneOf: [
                {
                    properties: {
                        fileType: { enum: ['.xlsx'] },
                        excelTabs: {
                            type: 'array',
                            title: 'Excel sheet tabs option',
                            items: {
                                type: 'object',
                                properties: {
                                    name: {
                                        type: 'string',
                                        title: 'Sheet tab name',
                                    },
                                    showFundName: {
                                        type: 'boolean',
                                        title: 'Show fund and series name in first column',
                                    },
                                },
                            },
                        },
                    },
                },
                {
                    properties: {
                        fileType: { enum: ['.csv'] },
                        csvOptions: {
                            type: 'object',
                            properties: {
                                showFundName: {
                                    type: 'boolean',
                                    title: 'Show fund and series name in first column',
                                },
                            },
                        },
                    },
                },
            ],
        },
        transformData: {
            oneOf: [
                {
                    properties: {
                        transformData: { enum: [true] },
                        functionToTransformData: {
                            type: 'string',
                            description: 'Function to transform Data',
                            default: 'return (data) => { return data}',
                        },
                    },
                },
            ],
        },
        isUseIntegratedDatePicker: {
            oneOf: [
                {
                    properties: {
                        isUseIntegratedDatePicker: {
                            enum: [true],
                        },
                        integratedDatePickerOptions: {
                            title: 'Integrated Date Picker Options',
                            type: 'object',
                            required: [],
                            properties: {
                                datePickerIconPosition: {
                                    type: 'string',
                                    title: 'Date Picker Icon position',
                                    enum: ['Right', 'Left'],
                                    default: 'Right',
                                },
                                datePickerIconType: {
                                    type: 'string',
                                    title: 'Date Picker Icon type',
                                    enum: ['FontAwesome Icon', 'Image'],
                                    default: 'FontAwesome Icon',
                                },
                                dateFormat: {
                                    type: 'string',
                                    title: 'Enter date format',
                                    default: 'MMMM d, yyyy',
                                    description: 'for example- MMMM d, yyyy',
                                },
                                startDateLabel: {
                                    type: 'string',
                                    title: 'Enter start date label',
                                    default: 'Start date',
                                },
                                endDateLabel: {
                                    type: 'string',
                                    title: 'Enter end date label',
                                    default: 'End date',
                                },
                            },
                            dependencies: {
                                datePickerIconType: {
                                    oneOf: [
                                        {
                                            properties: {
                                                datePickerIconType: {
                                                    enum: [`FontAwesome Icon`],
                                                },
                                                datePickerIcon: {
                                                    type: 'string',
                                                    title: `Date 
                                                    Picker Icon name`,
                                                    default: 'fa-calendar',
                                                },
                                            },
                                        },
                                        {
                                            properties: {
                                                datePickerIconType: {
                                                    enum: ['Image'],
                                                },
                                                datePickerImage: {
                                                    type: 'string',
                                                    title: `
                                                        Date Picker
                                                        Image url`,
                                                },
                                            },
                                        },
                                    ],
                                },
                            },
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            ...tableSchemaGroups[0].Values,
            'dateDatakey',
            'filterButtonName',
            'startDateLabel',
            'endDateLabel',
            'downloadButtonName',
            'displayDownloadButtonTop',
            'downloadFileName',
            'hideColumnsInCsv',
            'fileType',
            'excelTabs',
            'csvOptions',
            'isUseIntegratedDatePicker',
            'integratedDatePickerOptions',
        ],
        'Transform Data': ['transformData', 'functionToTransformData'],
    },
];

const PricesTableWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        chartTitles: {
            items: {
                headerText: {
                    'ui:widget': 'ResourcePicker',
                },
            },
        },
        noDataMessage: {
            'ui:widget': 'ResourcePicker',
        },
        newFundDataMessage: {
            'ui:widget': 'ResourcePicker',
        },
        emptyRowsMessage: {
            'ui:widget': 'ResourcePicker',
        },
        isUseIntegratedDatePicker: { 'ui:widget': 'CustomCheckbox' },
        functionToTransformData: { 'ui:widget': 'JSEditor' },
        integratedDatePickerOptions: {
            datePickerIconPosition: { 'ui:widget': 'CustomSelect' },
            datePickerIconType: { 'ui:widget': 'CustomSelect' },
            dateFormat: { 'ui:widget': 'CustomTextInput' },
            startDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            endDateLabel: { 'ui:widget': 'ResourcePicker', 'ui:placeholder': '{{ }}' },
            dataKey: { 'ui:widget': 'CustomTextInput' },
            datePickerIcon: { 'ui:widget': 'CustomTextInput' },
            datePickerImage: {
                'ui:placeholder': 'https://',
                'ui:widget': 'MediaPicker',
                'ui:options': {
                    isImagePicker: true,
                },
            },
        },
    },
    templateView: PricesTable,
    id: 'PricesTable',
    group: 'table',
    contentPlaceHolders: [],
    initialData: () => ({
        dateDatakey: 'effectiveDate',
    }),
};

export default PricesTableWidget;
